import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import DrawerToggleButton from "../sideDrawer/DrawerToggleButton";
import { trackEvent, trackButtonClick } from "../../utils/analytics";
import "w3-css/3/w3.css";
import "./toolbar.css";

const ToolbarEnhanced = props => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const navigate = useNavigate();

  // Search suggestions data
  const conversionTypes = [
    { name: "Area Conversion", path: "/conversion/area", keywords: ["area", "square", "meter", "feet", "acre", "hectare"], icon: "fa-expand" },
    { name: "Length Conversion", path: "/conversion/length", keywords: ["length", "distance", "meter", "feet", "inch", "kilometer", "mile"], icon: "fa-arrows-h" },
    { name: "Speed Conversion", path: "/conversion/speed", keywords: ["speed", "velocity", "mph", "kph", "knots", "mach"], icon: "fa-tachometer" },
    { name: "Temperature Conversion", path: "/conversion/temperature", keywords: ["temperature", "celsius", "fahrenheit", "kelvin"], icon: "fa-thermometer-half" },
    { name: "Time Conversion", path: "/conversion/time", keywords: ["time", "seconds", "minutes", "hours", "days"], icon: "fa-clock-o" },
    { name: "Volume Conversion", path: "/conversion/volume", keywords: ["volume", "liter", "gallon", "cubic", "fluid"], icon: "fa-cube" },
    { name: "Weight Conversion", path: "/conversion/weight", keywords: ["weight", "mass", "kilogram", "pound", "ounce", "gram"], icon: "fa-balance-scale" }
  ];

  // Load favorites from localStorage on component mount
  useEffect(() => {
    const savedFavorites = localStorage.getItem('conversion-favorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  // Save favorites to localStorage whenever favorites change
  useEffect(() => {
    localStorage.setItem('conversion-favorites', JSON.stringify(favorites));
  }, [favorites]);

  // Toggle favorite status
  const toggleFavorite = (conversionType) => {
    setFavorites(prev => {
      const isCurrentlyFavorite = prev.some(fav => fav.path === conversionType.path);
      if (isCurrentlyFavorite) {
        // Track removing from favorites
        trackEvent('remove_favorite', 'Favorites', conversionType.name);
        return prev.filter(fav => fav.path !== conversionType.path);
      } else {
        // Track adding to favorites
        trackEvent('add_favorite', 'Favorites', conversionType.name);
        return [...prev, conversionType];
      }
    });
  };

  // Check if a conversion type is favorited
  const isFavorite = (path) => {
    return favorites.some(fav => fav.path === path);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.length > 0) {
      const filtered = conversionTypes.filter(type =>
        type.name.toLowerCase().includes(query.toLowerCase()) ||
        type.keywords.some(keyword => keyword.toLowerCase().includes(query.toLowerCase()))
      );
      setSearchSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  // Handle search suggestion click
  const handleSuggestionClick = (path) => {
    // Track search suggestion click
    const suggestion = conversionTypes.find(type => type.path === path);
    if (suggestion) {
      trackEvent('search_suggestion_click', 'Navigation', suggestion.name);
    }
    navigate(path);
    setSearchQuery("");
    setShowSuggestions(false);
  };

  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchSuggestions.length > 0) {
      // Track search usage
      trackEvent('search_used', 'Navigation', `${searchQuery} -> ${searchSuggestions[0].name}`);
      navigate(searchSuggestions[0].path);
      setSearchQuery("");
      setShowSuggestions(false);
    }
  };

  // Toggle theme
  const toggleTheme = () => {
    const newTheme = !isDarkTheme;
    setIsDarkTheme(newTheme);
    document.body.classList.toggle('dark-theme');
    // Track theme usage
    trackEvent('theme_toggle', 'UI', newTheme ? 'dark' : 'light');
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-section')) {
        setShowSuggestions(false);
      }
      if (!event.target.closest('.nav-dropdown')) {
        setIsDropdownOpen(false);
      }
      if (!event.target.closest('.favorites-section')) {
        setShowFavorites(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Toggle dropdown
  const toggleDropdown = (e) => {
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Toggle favorites dropdown
  const toggleFavorites = (e) => {
    e.stopPropagation();
    setShowFavorites(!showFavorites);
  };

  // Handle dropdown link clicks
  const handleDropdownLinkClick = () => {
    setIsDropdownOpen(false);
  };

  // Handle favorites navigation
  const handleFavoriteClick = (path) => {
    navigate(path);
    setShowFavorites(false);
  };

  return (
    <header className={`modern-toolbar ${isDarkTheme ? 'dark-theme' : ''}`}>
      <div className="toolbar-container">
        {/* Logo/Brand Section */}
        <div className="toolbar-brand">
          <NavLink to="/" className="brand-link">
            <div className="brand-logo">
              <i className="fa fa-calculator" />
            </div>
            <div className="brand-text">
              <span className="brand-name">KAYANNOU</span>
              <span className="brand-tagline">Unit Converters</span>
            </div>
          </NavLink>
        </div>

        {/* Main Navigation */}
        <nav className="toolbar-navigation">
          <div className="nav-group">
            <div className="nav-dropdown">
              <button className="nav-dropdown-btn" onClick={toggleDropdown}>
                <i className="fa fa-th-large"></i>
                Converters
                <i className={`fa fa-caret-${isDropdownOpen ? 'up' : 'down'}`}></i>
              </button>
              <div className={`nav-dropdown-content ${isDropdownOpen ? 'show' : ''}`}>
                <div className="dropdown-section">
                  <h4>Measurement Tools</h4>
                  <div className="nav-link-with-favorite">
                    <NavLink
                      to="/conversion/area"
                      className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
                      onClick={handleDropdownLinkClick}
                    >
                      <i className="fa fa-expand"></i>
                      Area Conversion
                    </NavLink>
                    <button
                      className={`favorite-toggle ${isFavorite('/conversion/area') ? 'favorited' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(conversionTypes.find(type => type.path === '/conversion/area'));
                      }}
                      title={isFavorite('/conversion/area') ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <i className={`fa ${isFavorite('/conversion/area') ? 'fa-star' : 'fa-star-o'}`}></i>
                    </button>
                  </div>
                  <div className="nav-link-with-favorite">
                    <NavLink
                      to="/conversion/length"
                      className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
                      onClick={handleDropdownLinkClick}
                    >
                      <i className="fa fa-arrows-h"></i>
                      Length Conversion
                    </NavLink>
                    <button
                      className={`favorite-toggle ${isFavorite('/conversion/length') ? 'favorited' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(conversionTypes.find(type => type.path === '/conversion/length'));
                      }}
                      title={isFavorite('/conversion/length') ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <i className={`fa ${isFavorite('/conversion/length') ? 'fa-star' : 'fa-star-o'}`}></i>
                    </button>
                  </div>
                  <div className="nav-link-with-favorite">
                    <NavLink
                      to="/conversion/volume"
                      className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
                      onClick={handleDropdownLinkClick}
                    >
                      <i className="fa fa-cube"></i>
                      Volume Conversion
                    </NavLink>
                    <button
                      className={`favorite-toggle ${isFavorite('/conversion/volume') ? 'favorited' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(conversionTypes.find(type => type.path === '/conversion/volume'));
                      }}
                      title={isFavorite('/conversion/volume') ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <i className={`fa ${isFavorite('/conversion/volume') ? 'fa-star' : 'fa-star-o'}`}></i>
                    </button>
                  </div>
                  <div className="nav-link-with-favorite">
                    <NavLink
                      to="/conversion/weight"
                      className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
                      onClick={handleDropdownLinkClick}
                    >
                      <i className="fa fa-balance-scale"></i>
                      Weight Conversion
                    </NavLink>
                    <button
                      className={`favorite-toggle ${isFavorite('/conversion/weight') ? 'favorited' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(conversionTypes.find(type => type.path === '/conversion/weight'));
                      }}
                      title={isFavorite('/conversion/weight') ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <i className={`fa ${isFavorite('/conversion/weight') ? 'fa-star' : 'fa-star-o'}`}></i>
                    </button>
                  </div>
                </div>
                <div className="dropdown-section">
                  <h4>Physical Properties</h4>
                  <div className="nav-link-with-favorite">
                    <NavLink
                      to="/conversion/speed"
                      className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
                      onClick={handleDropdownLinkClick}
                    >
                      <i className="fa fa-tachometer"></i>
                      Speed Conversion
                    </NavLink>
                    <button
                      className={`favorite-toggle ${isFavorite('/conversion/speed') ? 'favorited' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(conversionTypes.find(type => type.path === '/conversion/speed'));
                      }}
                      title={isFavorite('/conversion/speed') ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <i className={`fa ${isFavorite('/conversion/speed') ? 'fa-star' : 'fa-star-o'}`}></i>
                    </button>
                  </div>
                  <div className="nav-link-with-favorite">
                    <NavLink
                      to="/conversion/temperature"
                      className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
                      onClick={handleDropdownLinkClick}
                    >
                      <i className="fa fa-thermometer-half"></i>
                      Temperature Conversion
                    </NavLink>
                    <button
                      className={`favorite-toggle ${isFavorite('/conversion/temperature') ? 'favorited' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(conversionTypes.find(type => type.path === '/conversion/temperature'));
                      }}
                      title={isFavorite('/conversion/temperature') ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <i className={`fa ${isFavorite('/conversion/temperature') ? 'fa-star' : 'fa-star-o'}`}></i>
                    </button>
                  </div>
                  <div className="nav-link-with-favorite">
                    <NavLink
                      to="/conversion/time"
                      className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
                      onClick={handleDropdownLinkClick}
                    >
                      <i className="fa fa-clock-o"></i>
                      Time Conversion
                    </NavLink>
                    <button
                      className={`favorite-toggle ${isFavorite('/conversion/time') ? 'favorited' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(conversionTypes.find(type => type.path === '/conversion/time'));
                      }}
                      title={isFavorite('/conversion/time') ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <i className={`fa ${isFavorite('/conversion/time') ? 'fa-star' : 'fa-star-o'}`}></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Access Navigation */}
          <div className="quick-nav">
            <NavLink to="/conversion/length" className={({ isActive }) => isActive ? "quick-nav-link active" : "quick-nav-link"}>
              Length
            </NavLink>
            <NavLink to="/conversion/area" className={({ isActive }) => isActive ? "quick-nav-link active" : "quick-nav-link"}>
              Area
            </NavLink>
            <NavLink to="/conversion/temperature" className={({ isActive }) => isActive ? "quick-nav-link active" : "quick-nav-link"}>
              Temperature
            </NavLink>
          </div>
        </nav>

        {/* Right Section */}
        <div className="toolbar-actions">
          <div className="search-section" onClick={e => e.stopPropagation()}>
            <form onSubmit={handleSearchSubmit} className="search-box">
              <input
                type="text"
                placeholder="Search conversions..."
                className="search-input"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => searchQuery && setShowSuggestions(true)}
              />
              <i className="fa fa-search"></i>

              {/* Search Suggestions */}
              {showSuggestions && searchSuggestions.length > 0 && (
                <div className="search-suggestions">
                  {searchSuggestions.slice(0, 5).map((suggestion, index) => (
                    <div
                      key={index}
                      className="search-suggestion"
                      onClick={() => handleSuggestionClick(suggestion.path)}
                    >
                      <i className="fa fa-search"></i>
                      {suggestion.name}
                    </div>
                  ))}
                </div>
              )}
            </form>
          </div>

          <div className="action-buttons">
            <div className="favorites-section" onClick={e => e.stopPropagation()}>
              <button
                className={`action-btn favorites-btn ${favorites.length > 0 ? 'has-favorites' : ''}`}
                title="Favorites"
                onClick={toggleFavorites}
              >
                <i className="fa fa-star"></i>
                {favorites.length > 0 && <span className="favorites-count">{favorites.length}</span>}
              </button>

              {/* Favorites Dropdown */}
              {showFavorites && (
                <div className="favorites-dropdown">
                  <div className="favorites-header">
                    <h4>
                      <i className="fa fa-star"></i>
                      Your Favorites
                    </h4>
                  </div>
                  <div className="favorites-content">
                    {favorites.length === 0 ? (
                      <div className="no-favorites">
                        <i className="fa fa-star-o"></i>
                        <p>No favorites yet</p>
                        <small>Click the star icon next to any converter to add it to your favorites</small>
                      </div>
                    ) : (
                      <div className="favorites-list">
                        {favorites.map((favorite, index) => (
                          <div key={index} className="favorite-item">
                            <button
                              className="favorite-link"
                              onClick={() => handleFavoriteClick(favorite.path)}
                            >
                              <i className={`fa ${favorite.icon}`}></i>
                              <span>{favorite.name}</span>
                            </button>
                            <button
                              className="remove-favorite"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(favorite);
                              }}
                              title="Remove from favorites"
                            >
                              <i className="fa fa-times"></i>
                            </button>
                          </div>
                        ))}
                        <div className="favorites-actions">
                          <button
                            className="clear-favorites"
                            onClick={() => setFavorites([])}
                            title="Clear all favorites"
                          >
                            <i className="fa fa-trash"></i>
                            Clear All
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <button
              className="action-btn theme-btn"
              title="Toggle Theme"
              onClick={toggleTheme}
            >
              <i className={`fa ${isDarkTheme ? 'fa-sun-o' : 'fa-moon-o'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="mobile-menu-toggle">
          <DrawerToggleButton click={props.drawerClickHandler} />
        </div>
      </div>

      {/* Mobile Search Bar */}
      <div className="mobile-search">
        <form onSubmit={handleSearchSubmit} className="mobile-search-box">
          <input
            type="text"
            placeholder="Search conversions..."
            className="mobile-search-input"
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <i className="fa fa-search"></i>
        </form>
      </div>
    </header>
  );
};

export default ToolbarEnhanced;
